import { useMutation, useQuery } from '@tanstack/react-query';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { Outlet, useLocation, useMatch } from 'react-router-dom';

import {
  type Entity,
  identityEntityCreateBusinessRootEntityMutation,
  identityEntityCreatePersonRootEntityMutation,
  identityEntityGetRootEntityOptions,
  identityEntityUpdateBusinessEntityMutation,
  identityEntityUpdatePersonEntityMutation,
} from '@column/api';
import { Button, Icon } from '@column/column-ui-kit';

import { CustomPageContext, PageTabs } from '~/app/Layout';
import { PageActions } from '~/app/Layout/PageFrameContext';
import { ROUTE } from '~/app/routes';
import { useFormErrorHandler } from '~/hooks/useFormErrorHandler';
import { useNavigate } from '~/lib/navigation';
import { useHelpSidebarStore } from '~/stores/HelpSidebar';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { ApiError } from '~/typings/API';
import { ApiErrCode } from '~/typings/enum';

import { Mode, RootEntityOutletContext } from './_util/RootEntityContext';
import {
  BusinessEntityFormValues,
  EntityFormType,
  PersonEntityFormValues,
  buildBusinessCreateRequest,
  buildBusinessUpdateRequest,
  buildPersonCreateRequest,
  buildPersonUpdateRequest,
  createDefaultPersonFormValues,
  createInitialBusinessFormState,
  getBusinessFormValuesFromEntity,
  getPersonFormValuesFromEntity,
  useEntityForm,
} from './_util/useEntityForm';
import { EntityTypes } from './Edit';

const createModeRequestOptions = (platformId: string, mode: Mode) => {
  const headers = {
    'Platform-Id': platformId,
    'Live-Mode': mode === Mode.Live ? 'Yes' : 'No',
  };

  return {
    headers,
    fetch: (request: Request) => {
      const requestHeaders = new Headers(request.headers);
      Object.entries(headers).forEach(([key, value]) => requestHeaders.set(key, value));
      return fetch(new Request(request, { headers: requestHeaders }));
    },
  };
};

const shouldNotRetryOnNotFound = (failureCount: number, error: unknown) => {
  const apiError = error as Partial<ApiError> | undefined;
  if (String(apiError?.code) === ApiErrCode.ResourceNotFound.toString()) {
    return false;
  }
  return failureCount < 3;
};

export const PageRootEntitySection: FC<CustomPageContext> = ({ entries }) => {
  const { currentPlatform, isSandbox, isAuthorized } = useSessionStore();
  const openHelpSidebar = useHelpSidebarStore((state) => state.openHelpSidebar);
  const navigate = useNavigate();
  const location = useLocation();
  const isLiveRoute = useMatch(ROUTE.ENTITIES_ROOT_ENTITY_LIVE);
  const addSuccessNotification = useNotificationStore((state) => state.addSuccessNotification);
  const addDangerNotification = useNotificationStore((state) => state.addDangerNotification);
  const { handleSubmitErrors } = useFormErrorHandler();

  const mode = useMemo(() => (isLiveRoute ? Mode.Live : Mode.Sandbox), [isLiveRoute]);
  const [formType, setFormType] = useState<EntityTypes>();

  const createRootPersonMutation = useMutation(identityEntityCreatePersonRootEntityMutation());
  const createRootBusinessMutation = useMutation(identityEntityCreateBusinessRootEntityMutation());
  const updateRootPersonMutation = useMutation(identityEntityUpdatePersonEntityMutation());
  const updateRootBusinessMutation = useMutation(identityEntityUpdateBusinessEntityMutation());

  const sandboxRootQueryOptions = identityEntityGetRootEntityOptions({
    path: { platform_id: currentPlatform?.id ?? '' },
    ...createModeRequestOptions(currentPlatform?.id ?? '', Mode.Sandbox),
  });
  const {
    data: sandboxRootEntity,
    isLoading: isSandboxLoading,
    refetch: refetchSandbox,
  } = useQuery({
    ...sandboxRootQueryOptions,
    enabled: !!currentPlatform?.id,
    staleTime: 5 * 60 * 1000,
    retry: shouldNotRetryOnNotFound,
  });

  const liveRootQueryOptions = identityEntityGetRootEntityOptions({
    path: { platform_id: currentPlatform?.id ?? '' },
    ...createModeRequestOptions(currentPlatform?.id ?? '', Mode.Live),
  });
  const {
    data: liveRootEntity,
    isLoading: isLiveLoading,
    refetch: refetchLive,
  } = useQuery({
    ...liveRootQueryOptions,
    enabled: !!currentPlatform?.id && !!currentPlatform?.isLiveEnabled,
    staleTime: 5 * 60 * 1000,
    retry: shouldNotRetryOnNotFound,
  });

  const rootEntityResponse = mode === Mode.Sandbox ? sandboxRootEntity : liveRootEntity;
  const form = useEntityForm(formType);

  const currentRootType = useMemo<EntityTypes | undefined>(() => {
    if (rootEntityResponse?.person_details) return EntityFormType.Person;
    if (rootEntityResponse?.business_details) return EntityFormType.Business;
    return undefined;
  }, [rootEntityResponse]);

  const resetForm = useCallback(
    (entityType: EntityTypes, entity: Entity | null) => {
      if (entity) {
        const values =
          entityType === EntityFormType.Person
            ? getPersonFormValuesFromEntity(entity)
            : getBusinessFormValuesFromEntity(entity);
        form.reset(values);
      } else {
        const values =
          entityType === EntityFormType.Person ? createDefaultPersonFormValues() : createInitialBusinessFormState();
        form.reset(values);
      }
    },
    [form]
  );

  useEffect(() => {
    if (location.pathname === ROUTE.ENTITIES_ROOT_ENTITY) {
      navigate(isSandbox ? ROUTE.ENTITIES_ROOT_ENTITY_SANDBOX : ROUTE.ENTITIES_ROOT_ENTITY_LIVE, { replace: true });
    }
  }, [isSandbox, location.pathname, navigate]);

  useEffect(() => {
    if (currentRootType && currentRootType !== formType) {
      setFormType(currentRootType);
    }
  }, [currentRootType, formType]);

  const refetchCurrent = async () => {
    if (mode === Mode.Sandbox) {
      await refetchSandbox();
    } else {
      await refetchLive();
    }
  };

  const handleSubmit = form.handleSubmit(async () => {
    if (!formType) {
      addDangerNotification({ content: 'Please select an entity type.' });
      return;
    }

    try {
      if (formType === EntityFormType.Person) {
        const payload = buildPersonUpdateRequest(form.getValues() as PersonEntityFormValues);

        if (rootEntityResponse?.person_details && rootEntityResponse.id) {
          const response = await updateRootPersonMutation.mutateAsync({
            path: { entity_id: rootEntityResponse.id },
            body: payload,
            ...createModeRequestOptions(currentPlatform?.id ?? '', mode),
          });
          addSuccessNotification({
            content: `${response.person_details?.first_name ?? ''} ${response.person_details?.last_name ?? ''}`.trim()
              ? `${response.person_details?.first_name ?? ''} ${response.person_details?.last_name ?? ''} updated`
              : 'Root entity updated',
          });
          await refetchCurrent();
        } else {
          const createPayload = buildPersonCreateRequest(form.getValues() as PersonEntityFormValues);
          const response = await createRootPersonMutation.mutateAsync({
            path: { platform_id: currentPlatform?.id ?? '' },
            body: createPayload,
            ...createModeRequestOptions(currentPlatform?.id ?? '', mode),
          });
          addSuccessNotification({
            content: `${response.person_details?.first_name ?? ''} ${response.person_details?.last_name ?? ''}`.trim()
              ? `${response.person_details?.first_name ?? ''} ${response.person_details?.last_name ?? ''} created`
              : 'Root entity created',
          });
          await refetchCurrent();
          setFormType(EntityFormType.Person);
        }
      } else {
        const businessValues = form.getValues() as BusinessEntityFormValues;

        if (rootEntityResponse?.business_details && rootEntityResponse.id) {
          const payload = buildBusinessUpdateRequest(businessValues);
          const response = await updateRootBusinessMutation.mutateAsync({
            path: { entity_id: rootEntityResponse.id },
            body: payload,
            ...createModeRequestOptions(currentPlatform?.id ?? '', mode),
          });
          addSuccessNotification({
            content: response.business_details?.business_name
              ? `${response.business_details.business_name} updated`
              : 'Root entity updated',
          });
          await refetchCurrent();
        } else {
          const payload = buildBusinessCreateRequest(businessValues, { allowEinPending: false });
          const response = await createRootBusinessMutation.mutateAsync({
            path: { platform_id: currentPlatform?.id ?? '' },
            body: payload,
            ...createModeRequestOptions(currentPlatform?.id ?? '', mode),
          });
          addSuccessNotification({
            content: response.business_details?.business_name
              ? `${response.business_details.business_name} created`
              : 'Root entity created',
          });
          await refetchCurrent();
          setFormType(EntityFormType.Business);
        }
      }
    } catch (error: any) {
      const message = error?.message ?? 'Failed to save root entity';
      addDangerNotification({ content: message });
    }
  }, handleSubmitErrors);

  const headerRootEntity = mode === Mode.Sandbox ? sandboxRootEntity : liveRootEntity;
  const headerHasRootEntity = Boolean(headerRootEntity);

  const tabEntries = [
    {
      label: 'Live',
      path: ROUTE.ENTITIES_ROOT_ENTITY_LIVE,
    },
    {
      label: 'Sandbox',
      path: ROUTE.ENTITIES_ROOT_ENTITY_SANDBOX,
    },
  ];

  return (
    <FormProvider {...form}>
      <PageTabs entries={entries ?? tabEntries} />
      <PageActions>
        <Button
          icon={<Icon.CircleQuestionmark />}
          onlyIcon
          onClick={() => openHelpSidebar('RootEntity')}
          variant="subtle"
          size="small"
        />
        {mode === Mode.Sandbox && (
          <Button
            onClick={handleSubmit}
            isDisabled={!isAuthorized({ permission: 'entities', permissionLevel: 'write' })}
            size="small"
            icon={!headerHasRootEntity && <Icon.Plus />}
          >
            {headerHasRootEntity ? 'Save Changes' : 'Create Root Entity'}
          </Button>
        )}
      </PageActions>
      <Outlet
        context={
          {
            mode,
            formType,
            sandboxRootEntity,
            liveRootEntity,
            isSandboxLoading,
            isLiveLoading,
            isSandbox: isSandbox ?? false,
            handleSubmit,
            setFormType,
            resetForm,
          } satisfies RootEntityOutletContext
        }
      />
    </FormProvider>
  );
};
